import type { Messages } from './zh-TW';

/** English strings.  Typed against the zh-TW catalogue, so a missing or
 *  misspelt key is a compile error rather than a blank label at runtime. */
export const en: Messages = {
  'app.name': 'JournalMap',
  'app.subtitle': 'Bibliometric mapping',
  'footer.institution': 'National Tsing Hua University, Department of Education and Learning Technology',
  'footer.advisor': 'Advisor: Prof. 陳美如',
  'footer.author': 'Built by 陳奕安, PhD candidate',
  'app.skipToContent': 'Skip to main content',

  'nav.new': 'New analysis',
  'nav.saved': 'My maps',

  'header.signIn': 'Sign in with Google',
  'header.signOut': 'Sign out',
  'header.signingIn': 'Signing in',
  'header.signInFailed': 'Sign-in did not go through: {message}',
  'header.quotaLabel': 'Quota',
  'header.quotaValue': '{remaining} of {limit} left',
  'header.quotaResets': 'resets {time}',
  'header.quotaLoading': 'loading',
  'header.quotaFailed': 'unavailable',
  'header.quotaTooltip': '{remaining} of {limit} runs left this period. Resets {time}.',
  'header.themeLabel': 'Appearance',
  'header.theme.system': 'Match system',
  'header.theme.light': 'Light',
  'header.theme.dark': 'Dark',
  'header.localeLabel': 'Language',

  'common.retry': 'Try again',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.copy': 'Copy link',
  'common.copied': 'Copied',
  'common.loading': 'Loading',
  'common.optional': 'optional',
  'common.unlimited': 'no limit',
  'common.on': 'On',
  'common.off': 'Off',
  'common.showAdvanced': 'Show advanced settings',
  'common.hideAdvanced': 'Hide advanced settings',
  'common.back': 'Back',
  'common.delete': 'Delete',
  'common.open': 'Open',
  'common.seconds': '{n}s',
  'common.dash': '—',

  'home.title': 'Build a map',
  'home.lead': 'Pick a data source, then pick an analysis. Defaults match VOSviewer.',
  'home.step1': '1. Data source',
  'home.step2': '2. Analysis and parameters',
  'home.step3': '3. Run',
  'home.run': 'Run analysis',
  'home.runNoSource': 'Choose a data source first.',
  'home.runNoAuth': 'Running an analysis needs a sign-in. Viewing a shared map does not.',
  'home.runNoQuota': 'All {limit} runs for this period are used. Resets {resets}.',
  'home.capTitle': 'Limits on the public service',
  'home.capBody': 'One run takes at most {docs} documents and {items} map items.',
  'home.capUpload': 'An uploaded export is capped at {size} per file.',
  'home.capWhy': 'The repulsion term of the VOS layout runs over every pair of nodes, so each of its thousand iterations is O(n²). The cap keeps a run under a minute, which is what lets the API answer synchronously. Larger corpora need a Cloud Tasks queue and a worker service.',

  'source.tab.openalex': 'OpenAlex query',
  'source.tab.upload': 'Upload an export',
  'source.openalex.hint': 'Fetch straight from OpenAlex. The result count is shown before you run.',
  'source.upload.hint': 'A Web of Science, Scopus, RIS or PubMed export. The format is sniffed.',

  'query.search.label': 'Search terms',
  'query.search.hint': 'Matched against title, abstract and full text; every word has to hit.',
  'query.search.placeholder': 'perovskite solar cell',
  'query.yearFrom.label': 'From year',
  'query.yearTo.label': 'To year',
  'query.year.hint': 'Publication year, both ends inclusive. Leave empty for no limit.',
  'query.venue.label': 'Journal or conference',
  'query.venue.hint': 'Words from the source name, e.g. Scientometrics.',
  'query.venue.placeholder': 'Scientometrics',
  'query.openAccess.label': 'Open access only',
  'query.openAccess.hint': 'Restrict to works OpenAlex marks as open access.',
  'query.institution.label': 'Institution',
  'query.institution.hint': 'Words from an author affiliation.',
  'query.institution.placeholder': 'National Tsing Hua University',
  'query.country.label': 'Country or region',
  'query.country.hint': 'Two-letter code of the affiliation country, e.g. TW, US, JP.',
  'query.country.placeholder': 'TW',
  'query.filter.label': 'Extra filters',
  'query.filter.hint': 'Raw OpenAlex filter syntax, comma separated, merged with the fields above.',
  'query.filter.placeholder': 'type:article,has_doi:true',
  'query.maxResults.label': 'Fetch at most',
  'query.maxResults.hint': 'Take this many of the matching works, most relevant first. Ceiling {cap}.',

  'count.idle': 'The result count appears once the query has something in it.',
  'count.loading': 'Counting',
  'count.result': '{count} works match',
  'count.capped': '{count} works match; the first {cap} will be used',
  'count.zero': 'Nothing matches. Widen the years or change the search terms.',
  'count.error': 'Could not get a count: {message}',
  'count.refresh': 'Count again',

  'upload.drop': 'Drop a file here, or click to choose one',
  'upload.dropActive': 'Release to load',
  'upload.formats': 'WoS plain text, Scopus CSV, RIS, PubMed nbib. At most {size} per file.',
  'upload.choose': 'Choose a file',
  'upload.remove': 'Remove',
  'upload.tooBig': 'That file is {size}, over the {limit} limit.',
  'upload.badType': 'The extension {ext} is not one of the supported formats.',

  'analysis.label': 'Analysis',
  'analysis.co_authorship': 'Co-authorship',
  'analysis.co_authorship.desc': 'How many documents two authors published together. Can be run on organizations or countries instead.',
  'analysis.keyword_co_occurrence': 'Keyword co-occurrence',
  'analysis.keyword_co_occurrence.desc': 'How often two keywords appear on the same document.',
  'analysis.terms': 'Term map',
  'analysis.terms.desc': 'Noun phrases pulled from titles and abstracts, filtered down to the most specific ones, then co-occurrence over those.',
  'analysis.citation': 'Direct citation',
  'analysis.citation.desc': 'Who cites whom inside the corpus. If references do not resolve, the map comes out empty.',
  'analysis.bibliographic_coupling': 'Bibliographic coupling',
  'analysis.bibliographic_coupling.desc': 'How many references two documents share. The references themselves need not be in the corpus.',
  'analysis.co_citation': 'Co-citation',
  'analysis.co_citation.desc': 'How often two cited references are cited together by the same documents.',

  'param.unit.label': 'Unit of analysis',
  'param.unit.hint.co_authorship': 'What a node is: the author, their organization, or the country of that organization.',
  'param.unit.hint.citation': 'Nodes can be the documents themselves, or citations rolled up to authors, sources, organizations or countries.',
  'param.unit.hint.co_citation': 'Whether a cited item is a work, its author, or the source it appeared in.',
  'unit.authors': 'Authors',
  'unit.organizations': 'Organizations',
  'unit.countries': 'Countries',
  'unit.documents': 'Documents',
  'unit.sources': 'Sources',
  'unit.references': 'Cited references',

  'param.keywordSource.label': 'Keyword source',
  'param.keywordSource.hint': 'Author keywords come from the DE field; index keywords are the ID field the database adds.',
  'keywordSource.author': 'Author keywords',
  'keywordSource.index': 'Index keywords',
  'keywordSource.both': 'Both',

  'param.counting.label': 'Counting method',
  'param.counting.hint': 'Full counting gives every co-occurrence pair a weight of one. Fractional counting divides a document’s weight by the number of pairs it contributes, so a long author list cannot drown out everything else.',
  'counting.full': 'Full counting',
  'counting.fractional': 'Fractional counting',

  'param.minOccurrences.label.occurrences': 'Minimum occurrences',
  'param.minOccurrences.label.documents': 'Minimum documents',
  'param.minOccurrences.label.citations': 'Minimum citations',
  'param.minOccurrences.hint.occurrences': 'Items occurring fewer times than this are left out. VOSviewer defaults to {default}.',
  'param.minOccurrences.hint.documents': 'Items with fewer documents than this are left out. VOSviewer defaults to {default}.',
  'param.minOccurrences.hint.citations': 'A reference cited fewer times than this cannot be positioned and is left out. VOSviewer defaults to {default}.',

  'param.minCitations.label': 'Minimum citations',
  'param.minCitations.hint': 'A second threshold on citation count. 0 means no filtering.',
  'param.maxItems.label': 'Maximum items',
  'param.maxItems.hint': 'If more items pass the thresholds than this, keep the heaviest. Ceiling {cap}.',

  'param.fields.label': 'Fields to read',
  'param.fields.hint': 'Which fields the noun phrases are pulled from.',
  'fields.title': 'Title',
  'fields.abstract': 'Abstract',
  'param.language.label': 'Language',
  'param.language.hint': 'Chinese is segmented by a separate service carrying the CKIP models, which is slower than English.',
  'language.english': 'English',
  'language.chinese': 'Chinese',
  'param.binaryCounting.label': 'Binary counting',
  'param.binaryCounting.hint': 'A term repeated within one document counts once. VOSviewer has this on.',
  'param.relevanceFraction.label': 'Relevance fraction',
  'param.relevanceFraction.hint': 'Terms are scored by how far their two-step co-occurrence profile diverges from the corpus average, and only the most specific are kept. VOSviewer keeps 0.60, that is 60%.',
  'param.nTerms.label': 'Maximum terms',
  'param.nTerms.hint': 'Truncate to this many after filtering. Empty means no truncation.',
  'param.cocitationKey.label': 'Cited-work identity',
  'param.cocitationKey.hint': 'The match key merges two spellings of one work into a single node. The reference string is what VOSviewer uses, and splits them into two.',
  'cocitationKey.matchkey': 'Match key',
  'cocitationKey.string': 'Reference string',

  'adv.title': 'Advanced: normalization, clustering, layout',
  'adv.note': 'These are VOSviewer’s defaults. Leave them alone without a reason.',
  'param.normalization.label': 'Link strength normalization',
  'param.normalization.hint': 'The normalized network feeds both the layout and the clustering; the map file still carries raw strengths.',
  'normalization.none': 'None',
  'normalization.association': 'Association strength',
  'normalization.fractionalization': 'Fractionalization',
  'normalization.linlog': 'LinLog / modularity',
  'param.resolution.label': 'Clustering resolution',
  'param.resolution.hint': 'Higher gives more, smaller clusters. VOSviewer defaults to 1.00.',
  'param.minClusterSize.label': 'Minimum cluster size',
  'param.minClusterSize.hint': 'Clusters below this size get merged away.',
  'param.mergeSmallClusters.label': 'Merge small clusters',
  'param.mergeSmallClusters.hint': 'Turn this off and small clusters stay as clusters of their own.',
  'param.clusterRandomStarts.label': 'Clustering random starts',
  'param.clusterRandomStarts.hint': 'Leiden runs this many times and the best-quality run wins.',
  'param.clusterIterations.label': 'Clustering iterations',
  'param.clusterIterations.hint': 'Iterations per random start.',
  'param.attraction.label': 'Attraction',
  'param.repulsion.label': 'Repulsion',
  'param.attraction.hint': 'The attraction exponent of the VOS layout, 2 by default.',
  'param.repulsion.hint': 'The repulsion exponent of the VOS layout, 1 by default. Changing either visibly changes the map.',
  'param.largestComponent.label': 'Largest component only',
  'param.largestComponent.hint': 'Drop islands that are not connected to the main body.',
  'param.seed.label': 'Random seed',
  'param.seed.hint': 'A fixed seed is what makes a map reproducible. VOSviewer defaults to 0.',

  'run.title': 'Running',
  'run.hint': 'A minute at the outside. Leaving this page stops the run.',
  'run.elapsed': '{seconds}s elapsed',
  'run.cancel': 'Stop',
  'run.stage.uploading': 'Uploading the file',
  'run.stage.queued': 'Queued',
  'run.stage.fetching': 'Fetching records',
  'run.stage.parsing': 'Parsing the file',
  'run.stage.extracting': 'Extracting terms',
  'run.stage.segmenting': 'Segmenting Chinese',
  'run.stage.counting': 'Counting co-occurrences',
  'run.stage.thresholding': 'Applying thresholds',
  'run.stage.normalizing': 'Normalizing',
  'run.stage.layout': 'VOS layout',
  'run.stage.clustering': 'Leiden clustering',
  'run.stage.writing': 'Writing files',
  'run.stage.done': 'Done',
  'run.stage.unknown': 'Working',

  'error.title': 'That run did not finish',
  'error.loadTitle': 'This map could not be loaded',
  'error.whatNow': 'What you can change',
  'error.quota_exceeded': 'All {limit} runs for this period are used. Resets {resets}.',
  'error.rate_limited': 'Too many requests in a short time. Wait {seconds} seconds.',
  'error.too_many_documents': 'This corpus has {actual} documents, over the limit of {limit}. Narrow the years or tighten the search.',
  'error.too_many_items': '{actual} items passed the thresholds, over the limit of {limit}. Raise the minimum occurrences, or lower the maximum items.',
  'error.upload_too_large': 'The file is {actual}, over the {limit} limit.',
  'error.parse_failed': 'That file could not be read: {message}',
  'error.empty_result': 'The query returned no documents.',
  'error.empty_map': 'No item passed the thresholds, so the map is empty. Lower the minimum occurrences.',
  'error.unauthenticated': 'This needs a sign-in.',
  'error.forbidden': 'You do not have access to this.',
  'error.not_found': 'Not found.',
  'error.network': 'Could not reach the server. Check the network and try again.',
  'error.timeout': 'The server took too long to answer.',
  'error.cancelled': 'Stopped.',
  'error.unsupported': 'This deployment cannot do that: {message}',
  'error.internal': 'The server failed: {message}',
  'error.unknown': 'Unexpected error: {message}',
  'error.notConfigured': 'This deployment has no {name} configured. Set it in the build environment and deploy again.',

  'result.backToHome': 'Build another',
  'result.loading': 'Loading the map',
  'result.meta.items': '{n} items',
  'result.meta.links': '{n} links',
  'result.meta.clusters': '{n} clusters',
  'result.meta.docs': '{n} documents',
  'result.view.label': 'View',
  'result.view.network': 'Network',
  'result.view.overlay': 'Overlay',
  'result.view.density': 'Item density',
  'result.view.clusterDensity': 'Cluster density',
  'result.overlay.label': 'Overlay value',
  'result.weight.label': 'Item size',
  'result.linkDensity.label': 'Link density',
  'result.linkDensity.hint': 'Draw only the strongest links. Lowering this makes a dense map readable.',
  'result.linkDensity.value': '{percent}%, {shown} of {total}',
  'result.legend.title': 'Clusters',
  'result.legend.cluster': 'Cluster {n}',
  'result.legend.count': '{n} items',
  'result.legend.none': 'This map carries no cluster assignment.',
  'result.detail.title': 'Item',
  'result.detail.empty': 'Click an item on the map and its numbers appear here.',
  'result.detail.cluster': 'Cluster',
  'result.detail.weights': 'Weights',
  'result.detail.scores': 'Scores',
  'result.detail.neighbours': 'Strongest links',
  'result.detail.noNeighbours': 'This item has no links.',
  'result.detail.openUrl': 'Open the source record',
  'result.detail.coordinates': 'Coordinates',
  'result.export.label': 'Export',
  'result.export.json': 'VOSviewer JSON',
  'result.export.map': 'Map file',
  'result.export.network': 'Network file',
  'result.export.png': 'PNG image',
  'result.export.hint': 'The JSON opens on VOSviewer Online. The map and network files open from the desktop File menu.',
  'result.export.failed': 'Export failed: {message}',
  'result.export.working': 'Exporting',
  'result.warnings.title': 'Worth knowing about this map',
  'result.build.title': 'Build summary',
  'result.build.counted': 'Items counted',
  'result.build.dropped': 'Dropped by thresholds',
  'result.build.quality': 'Clustering quality',
  'result.build.duration': 'Duration',
  'result.search.placeholder': 'Find an item',
  'result.search.noMatch': 'Nothing matches.',
  'result.panel.hide': 'Hide the side panel',
  'result.panel.show': 'Show the side panel',

  'saved.title': 'My maps',
  'saved.lead': 'Every run that finishes is kept here.',
  'saved.needAuth': 'Sign in to see your own maps.',
  'saved.empty': 'Nothing saved yet. Run an analysis and it shows up here.',
  'saved.loading': 'Loading the list',
  'saved.failed': 'Could not load the list: {message}',
  'saved.col.name': 'Name',
  'saved.col.analysis': 'Analysis',
  'saved.col.size': 'Size',
  'saved.col.created': 'Created',
  'saved.col.share': 'Public link',
  'saved.deleteConfirm': 'Delete “{name}”? This cannot be undone.',
  'saved.deleteFailed': 'Could not delete it: {message}',
  'saved.share.on': 'Public',
  'saved.share.off': 'Private',
  'saved.share.hint': 'Once on, anyone with the link can see this map without signing in.',
  'saved.share.failed': 'Could not change sharing: {message}',
  'saved.share.link': 'Public link',

  'public.badge': 'Shared map',
  'public.meta': '{analysis} · {items} items · {date}',
  'public.notFound': 'No map at this link. It may have been un-shared.',
  'public.cta': 'Build one of your own',

  'notFound.title': 'No such page',
  'notFound.body': 'The address may be wrong, or the page has moved.',
  'notFound.home': 'Go to the start',

  'crash.title': 'The interface crashed',
  'crash.body': 'This is a bug in the frontend, not something you did. A reload usually clears it.',
  'crash.reload': 'Reload',
};
